import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { defaultInstance } from '../api/httpClient';
import { toaster } from 'baseui/toast';
import type { InvitationType } from '../api/generated';

interface InvitationTypeListResponse {
  data: InvitationType[];
}

export const useInvitationTypes = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['invitation-types'],
    queryFn: async () => {
      const response = await defaultInstance<InvitationTypeListResponse>({
        url: '/invitation_type',
        method: 'GET',
      });
      return (response as unknown as InvitationTypeListResponse).data || [];
    },
  });

  return {
    invitationTypes: (data as InvitationType[] | undefined) || [],
    isLoading,
    error,
  };
};

export const useCreateInvitationType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<InvitationType>) => {
      return await defaultInstance({
        url: '/invitation_type',
        method: 'POST',
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitation-types'] });
      toaster.positive('Invitation type created successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to create invitation type', { autoHideDuration: 3000 });
    },
  });
};

export const useUpdateInvitationType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<InvitationType> }) => {
      return await defaultInstance({
        url: `/invitation_type/${id}`,
        method: 'PUT',
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitation-types'] });
      toaster.positive('Invitation type updated successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to update invitation type', { autoHideDuration: 3000 });
    },
  });
};

export const useDeleteInvitationType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      return await defaultInstance({
        url: `/invitation_type/${id}`,
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitation-types'] });
      toaster.positive('Invitation type deleted successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to delete invitation type', { autoHideDuration: 3000 });
    },
  });
};
